
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { Tenant } from '@repo/shared-types';
import { useToast } from '../../components/common/ToastContext';
import { tenantSchema } from '@repo/shared-types/leasing/tenantSchemas';
import { tenantNoteSchema } from '@repo/shared-types/common/noteSchemas';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';
import { getFriendlyErrorMessage } from '@repo/shared-utils';

export const useTenants = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['tenants', filters ?? {}],
    queryFn: async () => {
      const raw = await api.tenants.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Tenant[] }).data;
      const validation = validateEntityArray(tenantSchema, list);
      if (!validation.valid) {
        logger.error('Invalid tenants query response:', validation.errors);
        throw new Error('Invalid tenants data received from API');
      }
      return validation.data! as Tenant[];
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/** Use when filters include pageSize (and optionally page) for paginated list + total count. */
export const useTenantsPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['tenants', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.tenants.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Tenant[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: Tenant[]; total: number }).total;
      const validation = validateEntityArray(tenantSchema, list);
      if (!validation.valid) {
        logger.error('Invalid tenants query response:', validation.errors);
        throw new Error('Invalid tenants data received from API');
      }
      return { data: validation.data! as Tenant[], total };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useTenant = (id: string | undefined) => {
  return useQuery({
    queryKey: ['tenants', id],
    queryFn: async () => {
      const data = await api.tenants.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(tenantSchema, data);
        if (!validation.valid) {
          logger.error('Invalid tenant query response:', validation.errors);
          // Return null on validation failure
          throw new Error('Invalid tenant data received from API');
        }
        // Type assertion: Zod-validated data matches Tenant type structure
        return validation.data! as Tenant;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreateTenant = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (tenant: Omit<Tenant, 'id' | 'balance' | 'avatar'>) => api.tenants.create(tenant),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(tenantSchema, data);
        if (!validation.valid) {
          logger.error('Invalid tenant response from API:', validation.errors);
          showToast('Tenant created but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['tenants'] });
          return;
        }
        queryClient.invalidateQueries({ queryKey: ['tenants', data.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      showToast('Resident added successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to add resident', 'error');
    }
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) => api.tenants.update(id, data),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      if (data) {
        const validation = validateEntity(tenantSchema, data);
        if (!validation.valid) {
          logger.error('Invalid tenant response from API:', validation.errors);
          showToast('Tenant updated but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['tenants'] });
          return;
        }
        queryClient.invalidateQueries({ queryKey: ['tenants', data.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      showToast('Tenant profile updated', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update tenant', 'error');
    }
  });
};

export const useDeleteTenant = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.tenants.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      showToast('Tenant deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete tenant', 'error');
    }
  });
};

// --- TENANT NOTES ---

export const useTenantNotes = (tenantId: string) => {
  return useQuery({
    queryKey: ['tenants', 'notes', tenantId],
    queryFn: async () => {
      const data = await api.tenants.notes.list(tenantId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(tenantNoteSchema, data);
      if (!validation.valid) {
        logger.error('Invalid tenant notes query response:', validation.errors);
        return [] as import('@repo/shared-types/common/notes').TenantNote[];
      }
      return validation.data! as import('@repo/shared-types/common/notes').TenantNote[];
    },
    enabled: !!tenantId,
  });
};

export const useCreateTenantNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (note: { tenantId: string; content: string }) => api.tenants.notes.create(note),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', 'notes', data.tenantId] });
      showToast('Note created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create note', 'error');
    }
  });
};

export const useUpdateTenantNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { content: string } }) =>
      api.tenants.notes.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants', 'notes'] });
      showToast('Note updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update note', 'error');
    }
  });
};

export const useDeleteTenantNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.tenants.notes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants', 'notes'] });
      showToast('Note deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete note', 'error');
    }
  });
};

